import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Text, Tooltip, Checkbox } from '@fluentui/react-components';
import {
  EditRegular,
  SaveRegular,
  ArrowRightRegular,
  ArrowLeftRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { FormStep } from '../FormStep';
import { SpaceSelection } from '../SpaceSelection';
import { ReviewSummary } from '../ReviewSummary';
import { Confirmation } from '../Confirmation';
import { InlineFilterEditor } from '@components/common/InlineFilterEditor/InlineFilterEditor';
import { FloatingActionBar } from '@components/common/FloatingActionBar';
import locationIcon from '@/assets/icons/figma/step-1/location.svg';
import calendarIcon from '@/assets/icons/figma/step-1/calendar.svg';
import clockIcon from '@/assets/icons/figma/step-1/clock.svg';
import peopleIcon from '@/assets/icons/figma/step-1/people.svg';
import { useBookingFormStyles } from './BookingForm.styles';
import { formatDate, formatTime12Hour } from '@utils/dateFormat';
import type {
  FormConfiguration,
  SpaceDetails,
  StepCompletionMap,
  FieldDefinition,
} from '@store/models/booking';
import type { SpaceWithAvailability } from '@store/models';
import type { Building } from '@store/models/buildings/entities';

export interface BookingFormProps {
  config: FormConfiguration;
  formData: Record<string, unknown>;
  currentStep: number;
  highestVisitedStep: number; // Used for step navigation gating
  selectedSpace: SpaceDetails | null;
  availableSpaces: SpaceWithAvailability[];
  spacesLoading: boolean;
  validationErrors: Record<string, string>;
  submissionStatus: 'idle' | 'submitting' | 'submitted' | 'error';
  apiError: string | null;
  /** Submitted request reference id — surfaced on the thank-you summary. */
  requestId?: string | null;
  selectedBuilding?: Building | null;
  stepCompletionMap?: StepCompletionMap;
  buildingLoading?: boolean;
  resumeSpacesLoading?: boolean;
  onFieldChange: (key: string, value: unknown) => void;
  onNext: () => void;
  onBack: () => void;
  onSaveAsDraft: () => void;
  onDiscard: () => void; // For future discard dialog implementation
  onSubmit: () => void;
  canSubmit: boolean;
  canSaveDraft?: boolean;
  onStepClick: (stepIndex: number) => void;
  onSelectSpace: (spaceId: string) => void;
  onSetupChange?: (spaceId: string, setupCodes: string[]) => void;
  onBookAnother: () => void;
  onGoHome: () => void;
  onRetrySpaces?: () => void;
  onLoadMore?: () => void;
  onRetryBatch?: () => void;
  onAmenityFilterChange?: (amenities: string[]) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  batchError?: string | null;
  backDisabledOnReview?: boolean;
  filterFields?: FieldDefinition[];
  onInlineFieldChange?: (key: string, value: unknown) => void;
  onUpdateFilters?: () => Promise<boolean>;
  filterValidationErrors?: Record<string, string>;
}

export const BookingForm: React.FC<BookingFormProps> = ({
  config,
  formData,
  currentStep,
  highestVisitedStep: _highestVisitedStep,
  selectedSpace,
  availableSpaces,
  spacesLoading,
  validationErrors,
  submissionStatus,
  apiError,
  requestId,
  selectedBuilding,
  stepCompletionMap,
  buildingLoading,
  resumeSpacesLoading,
  onFieldChange,
  onNext,
  onBack,
  onSaveAsDraft,
  onDiscard: _onDiscard,
  onSubmit,
  canSubmit,
  canSaveDraft = true,
  onStepClick,
  onSelectSpace,
  onSetupChange,
  onBookAnother,
  onGoHome,
  onRetrySpaces,
  onLoadMore,
  onRetryBatch,
  onAmenityFilterChange,
  hasMore,
  isLoadingMore,
  batchError,
  backDisabledOnReview = false,
  filterFields,
  onInlineFieldChange,
  onUpdateFilters,
  filterValidationErrors = {},
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useBookingFormStyles();

  // Centers the floating action banner horizontally over the space-step cards
  // (the cards grid is shifted right by the persistent left nav rail, so the
  // viewport center does not coincide with the cards' center). We measure the
  // grid's center on layout/resize and feed an offset to the FloatingActionBar
  // via its existing rootLeft/rootRight CSS vars so the bar's inner — which
  // keeps its standard 75rem max-width — visually centers on the cards rather
  // than the viewport. No styling values appear inline; only CSS var values.
  const spaceContentRef = useRef<HTMLDivElement | null>(null);
  const [spaceCenterOffsetPx, setSpaceCenterOffsetPx] = useState<number>(0);
  const [spaceGridWidthPx, setSpaceGridWidthPx] = useState<number>(0);
  useEffect(() => {
    const wrapper = spaceContentRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      const grid = wrapper.querySelector<HTMLElement>('[role="list"]');
      const target = grid ?? wrapper;
      const rect = target.getBoundingClientRect();
      const cardCenter = (rect.left + rect.right) / 2;
      const viewportCenter = window.innerWidth / 2;
      setSpaceCenterOffsetPx(cardCenter - viewportCenter);
      setSpaceGridWidthPx(rect.width);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapper);
    const grid = wrapper.querySelector<HTMLElement>('[role="list"]');
    if (grid) ro.observe(grid);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [currentStep]);

  // Map the signed offset to the equivalent (rootLeft, rootRight) pair that
  // keeps a `justify-content: center` root visually centered on the cards.
  // Positive offset (cards right of viewport center) → push left edge in.
  const spaceBarRootLeft = spaceCenterOffsetPx > 0 ? `${2 * spaceCenterOffsetPx}px` : undefined;
  const spaceBarRootRight = spaceCenterOffsetPx < 0 ? `${-2 * spaceCenterOffsetPx}px` : undefined;

  // Match the floating action bar's inner width to the measured space-card grid
  // width so the Back/Save/Next footer spans exactly the same horizontal extent
  // as the space cards (instead of the shared 75rem inner cap).
  const spaceBarInnerMaxWidth = spaceGridWidthPx > 0 ? `${spaceGridWidthPx}px` : undefined;

  const [isEditing, setIsEditing] = useState(false);
  const [filterSnapshot, setFilterSnapshot] = useState<Record<string, unknown> | null>(null);

  const handleEditClick = useCallback(() => {
    const filterKeys = [
      'stateProvince',
      'buildingId',
      'eventDateRange',
      'eventTimeRange',
      'numberOfAttendees',
      'buildingName',
      'stateName',
    ];
    const snapshot: Record<string, unknown> = {};
    for (const key of filterKeys) {
      snapshot[key] = formData[key];
    }
    setFilterSnapshot(snapshot);
    setIsEditing(true);
  }, [formData]);

  const handleUpdateClick = useCallback(async () => {
    if (!onUpdateFilters) return;
    const success = await onUpdateFilters();
    if (success) {
      setIsEditing(false);
      setFilterSnapshot(null);
    }
  }, [onUpdateFilters]);

  const handleCancelClick = useCallback(() => {
    if (filterSnapshot && onInlineFieldChange) {
      for (const [key, value] of Object.entries(filterSnapshot)) {
        onInlineFieldChange(key, value);
      }
    }
    setIsEditing(false);
    setFilterSnapshot(null);
  }, [filterSnapshot, onInlineFieldChange]);

  if (submissionStatus === 'submitted') {
    return (
      <Confirmation
        onBookAnother={onBookAnother}
        onGoHome={onGoHome}
        formData={formData}
        selectedSpace={selectedSpace}
        selectedBuilding={selectedBuilding}
        requestId={requestId}
      />
    );
  }

  if (config.steps.length === 0) {
    return <div className={styles.emptyMessage}>{t('bookingForm.messages.noFormSteps')}</div>;
  }

  const step = config.steps[currentStep];
  if (!step) return null;

  const isFirstStep = currentStep === 0;
  const isSpaceStep = step.type === 'space-selection';
  const isReviewStep = step.type === 'review';

  // Arrangement style selection is mandatory whenever the selected space exposes
  // any room-layout options (including boardroom). Next stays disabled until the
  // user explicitly picks at least one arrangement.
  const selectedSpaceFull = selectedSpace
    ? (availableSpaces.find((s) => s.spaceId === selectedSpace.id) ?? null)
    : null;
  const requiresArrangementSelection =
    !!selectedSpaceFull &&
    (selectedSpaceFull.supportedRoomLayouts.length > 0 || selectedSpaceFull.isBoardRoom);
  const arrangementSelected =
    Array.isArray(formData.selectedSetups) && (formData.selectedSetups as unknown[]).length > 0;
  // spec 064 FR-005/FR-013: when the sum of selected arrangement capacities
  // exceeds the selected space's roomCapacity, the same condition that shows
  // the capacity warning banner MUST also block "Next".
  const selectedSetupsArr = Array.isArray(formData.selectedSetups)
    ? (formData.selectedSetups as string[])
    : [];
  const combinedCapacitySum = selectedSpaceFull
    ? selectedSpaceFull.supportedRoomLayouts
        .filter((layout) => selectedSetupsArr.includes(layout.code))
        .reduce((sum, layout) => sum + layout.capacity, 0)
    : 0;
  const combinedCapacityExceeds =
    !!selectedSpaceFull &&
    selectedSpaceFull.roomCapacity !== undefined &&
    selectedSetupsArr.length > 0 &&
    combinedCapacitySum > selectedSpaceFull.roomCapacity;
  const canProceedFromSpaceStep =
    (!requiresArrangementSelection || arrangementSelected) && !combinedCapacityExceeds;

  const getNextLabel = () => {
    if (isFirstStep) return t('bookingForm.buttons.viewAvailableSpaces');
    if (isReviewStep) return t('bookingForm.buttons.submit');
    return t('bookingForm.buttons.next');
  };

  const handleNextClick = () => {
    if (isReviewStep) {
      onSubmit();
    } else {
      onNext();
    }
  };

  // Summary bar for Step 2 (shows Step 1 values) — toggles between read-only and inline edit
  const renderSummaryBar = () => {
    if (!isSpaceStep) return null;

    if (isEditing && filterFields && onInlineFieldChange && onUpdateFilters) {
      return (
        <div className={styles.inlineEditContainer}>
          <InlineFilterEditor
            filterFields={filterFields}
            formData={formData}
            onFieldChange={onInlineFieldChange}
            onUpdate={handleUpdateClick}
            onCancel={handleCancelClick}
            validationErrors={filterValidationErrors}
            isUpdating={spacesLoading}
          />
        </div>
      );
    }

    const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
    const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;

    const stateLabel =
      selectedBuilding?.stateName ??
      (formData.stateName as string | undefined) ??
      (formData.stateProvince as string | undefined);
    const buildingLabel =
      selectedBuilding?.buildingName ?? (formData.buildingName as string | undefined);
    const locationText =
      [stateLabel, buildingLabel]
        .filter((part) => part && String(part).trim().length > 0)
        .join(', ') || '-';

    return (
      <div className={styles.summaryBar}>
        <div className={styles.summaryItem} data-testid="summary-item-location">
          <img src={locationIcon} alt="" aria-hidden="true" className={styles.summaryIcon} />
          <span className={styles.summaryLabel}>{t('bookingForm.review.location')}:</span>
          <span>{locationText}</span>
        </div>
        {dateRange?.start && (
          <div className={styles.summaryItem} data-testid="summary-item-date">
            <img src={calendarIcon} alt="" aria-hidden="true" className={styles.summaryIcon} />
            <span className={styles.summaryLabel}>{t('bookingForm.review.date')}:</span>
            <span>
              {formatDate(dateRange.start)}
              {dateRange.end
                ? ` ${t('bookingForm.review.rangeSeparator')} ${formatDate(dateRange.end)}`
                : ''}
            </span>
          </div>
        )}
        {timeRange?.start && (
          <div className={styles.summaryItem} data-testid="summary-item-time">
            <img src={clockIcon} alt="" aria-hidden="true" className={styles.summaryIcon} />
            <span className={styles.summaryLabel}>{t('bookingForm.review.time')}:</span>
            <span>
              {formatTime12Hour(timeRange.start)} {t('bookingForm.review.rangeSeparator')}{' '}
              {formatTime12Hour(timeRange.end)}
            </span>
          </div>
        )}
        {formData.numberOfAttendees != null && (
          <div className={styles.summaryItem} data-testid="summary-item-attendees">
            <img src={peopleIcon} alt="" aria-hidden="true" className={styles.summaryIcon} />
            <span className={styles.summaryLabel}>{t('bookingForm.review.attendees')}:</span>
            <span>{Number(formData.numberOfAttendees)}</span>
          </div>
        )}
        <BasicButton
          variant="outline"
          size="small"
          icon={<EditRegular />}
          onClick={handleEditClick}
          disabled={spacesLoading}
        >
          {t('bookingForm.buttons.edit')}
        </BasicButton>
      </div>
    );
  };

  const submitTooltip =
    isReviewStep && !canSubmit && stepCompletionMap
      ? t('bookingForm.review.submitIncomplete', {
          sections: stepCompletionMap.incompleteStepNames.join(', '),
        })
      : '';

  const renderStepContent = () => {
    switch (step.type) {
      case 'form':
        return (
          <FormStep
            step={step}
            formData={formData}
            validationErrors={validationErrors}
            onChange={onFieldChange}
            selectedBuilding={selectedBuilding}
          />
        );
      case 'space-selection':
        return (
          <>
            <SpaceSelection
              spaces={availableSpaces}
              loading={spacesLoading}
              error={apiError}
              selectedSpaceId={selectedSpace?.id ?? null}
              {...(typeof formData.numberOfAttendees === 'number' && {
                attendeeCount: formData.numberOfAttendees,
              })}
              {...(Array.isArray(formData.selectedSetups) && {
                selectedSetups: formData.selectedSetups as string[],
              })}
              onSelectSpace={onSelectSpace}
              {...(onSetupChange && { onSetupChange })}
              {...(onRetrySpaces && { onRetry: onRetrySpaces })}
              {...(onLoadMore && { onLoadMore })}
              {...(onRetryBatch && { onRetryBatch })}
              {...(onAmenityFilterChange && { onAmenityFilterChange })}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              batchError={batchError ?? null}
            />
          </>
        );
      case 'review':
        return (
          <ReviewSummary
            formData={formData}
            selectedSpace={selectedSpace}
            selectedBuilding={selectedBuilding}
            onEditStep={onStepClick}
            highestVisitedStep={_highestVisitedStep}
            stepCompletionMap={stepCompletionMap}
            buildingLoading={buildingLoading}
            spacesLoading={resumeSpacesLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.root}>
      {/* Main content - LEFT side per Figma */}
      <div className={styles.content}>
        {/*
          Step title and subtitle OUTSIDE the card.
          FR-006 / FR-007 (spec 099): hidden for `form`, `space-selection`, and
          `review` steps - the hero banner per step (FR-001) already conveys the
          step name and description. Confirmation/other step types may still
          render this title block.
        */}
        {step.type !== 'form' && step.type !== 'space-selection' && step.type !== 'review' && (
          <div className={styles.stepTitleSection}>
            <Text as="h2" className={styles.stepTitle}>
              {t(step.titleKey)}
            </Text>
            {step.descriptionKey && (
              <Text className={styles.stepSubtitle}>{t(step.descriptionKey)}</Text>
            )}
          </div>
        )}

        {isSpaceStep && renderSummaryBar()}

        {apiError && !isSpaceStep && (
          <div className={styles.errorBanner}>
            <span>{apiError}</span>
            {isReviewStep && submissionStatus === 'error' && (
              <BasicButton variant="transparent" size="small" onClick={handleNextClick}>
                {t('bookingForm.buttons.retry')}
              </BasicButton>
            )}
          </div>
        )}

        {/* Outer white card container */}
        {step.type === 'form' ? (
          <div className={styles.formOuterCard}>
            {/* Inner grey card with form fields */}
            <div className={styles.formInnerCard}>
              {renderStepContent()}
              {/* Figma 2026 footer: Back on LEFT with arrow icon; Save-as-draft + primary on RIGHT */}
              <div className={styles.cardFooter}>
                <div className={styles.cardFooterLeft}>
                  {!isFirstStep && (
                    <BasicButton
                      variant="outline"
                      size="medium"
                      icon={<ArrowLeftRegular />}
                      onClick={onBack}
                      disabled={submissionStatus === 'submitting'}
                    >
                      {t('bookingForm.buttons.back')}
                    </BasicButton>
                  )}
                </div>
                <div className={styles.cardFooterRight}>
                  <BasicButton
                    variant="outline"
                    size="medium"
                    icon={<SaveRegular />}
                    onClick={onSaveAsDraft}
                    disabled={!canSaveDraft}
                  >
                    {t('bookingForm.buttons.saveAsDraft')}
                  </BasicButton>
                  <BasicButton
                    variant="primary"
                    size="medium"
                    iconPosition="after"
                    icon={<ArrowRightRegular />}
                    onClick={handleNextClick}
                    disabled={submissionStatus === 'submitting' || (isReviewStep && !canSubmit)}
                  >
                    {submissionStatus === 'submitting' ? '...' : getNextLabel()}
                  </BasicButton>
                </div>
              </div>
            </div>
          </div>
        ) : isSpaceStep ? (
          <>
            {/* Ref wraps the space step content so we can measure the cards
                grid and center the floating action bar over it. */}
            <div ref={spaceContentRef} className={styles.spaceStepScrollClearance}>
              {renderStepContent()}
            </div>
            <FloatingActionBar
              visible
              hideOnFocusInputs
              bare
              ariaLabel={t('bookingForm.buttons.next')}
              rootLeft={spaceBarRootLeft}
              rootRight={spaceBarRootRight}
              {...(spaceBarInnerMaxWidth !== undefined && {
                innerMaxWidth: spaceBarInnerMaxWidth,
              })}
            >
              <div className={styles.spaceStepActionBanner}>
                <BasicButton
                  variant="outline"
                  size="medium"
                  icon={<ArrowLeftRegular />}
                  onClick={onBack}
                  disabled={submissionStatus === 'submitting'}
                >
                  {t('bookingForm.buttons.back')}
                </BasicButton>
                <div className={styles.spaceStepActionRight}>
                  <BasicButton
                    variant="outline"
                    size="medium"
                    icon={<SaveRegular />}
                    onClick={onSaveAsDraft}
                    disabled={!canSaveDraft}
                  >
                    {t('bookingForm.buttons.saveAsDraft')}
                  </BasicButton>
                  <BasicButton
                    variant="primary"
                    size="medium"
                    iconPosition="after"
                    icon={<ArrowRightRegular />}
                    onClick={onNext}
                    disabled={
                      !selectedSpace ||
                      !canProceedFromSpaceStep ||
                      submissionStatus === 'submitting'
                    }
                  >
                    {t('bookingForm.buttons.next')}
                  </BasicButton>
                </div>
              </div>
            </FloatingActionBar>
          </>
        ) : isReviewStep ? (
          <>
            {renderStepContent()}
            {!formData.policyAcknowledgement && (
              <div className={styles.policyBanner}>
                <Checkbox
                  label={
                    <span>
                      {t('bookingForm.policyAcknowledgement.label')}{' '}
                      <a
                        href="/policies/conference-room"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.policyLink}
                      >
                        {t('bookingForm.policyAcknowledgement.linkText')}
                      </a>
                      .
                    </span>
                  }
                  checked={false}
                  onChange={() => onFieldChange('policyAcknowledgement', true)}
                />
              </div>
            )}
            <div className={styles.navigation}>
              <div className={styles.navLeft}>
                <BasicButton
                  variant="outline"
                  size="medium"
                  icon={<ArrowLeftRegular />}
                  onClick={onBack}
                  disabled={submissionStatus === 'submitting' || backDisabledOnReview}
                >
                  {t('bookingForm.buttons.back')}
                </BasicButton>
              </div>
              <BasicButton
                variant="outline"
                size="medium"
                icon={<SaveRegular />}
                onClick={onSaveAsDraft}
                disabled={!canSaveDraft}
              >
                {t('bookingForm.buttons.saveAsDraft')}
              </BasicButton>
              <Tooltip
                content={submitTooltip}
                relationship="description"
                visible={!canSubmit && !!submitTooltip ? undefined : false}
              >
                <BasicButton
                  variant="primary"
                  size="medium"
                  onClick={handleNextClick}
                  disabled={submissionStatus === 'submitting' || (isReviewStep && !canSubmit)}
                >
                  {submissionStatus === 'submitting' ? '...' : getNextLabel()}
                </BasicButton>
              </Tooltip>
            </div>
          </>
        ) : (
          renderStepContent()
        )}
      </div>
    </div>
  );
};

BookingForm.displayName = 'BookingForm';
